import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Stage, Layer, Group, Line, Text } from 'react-konva';
//import { ROOM_SHAPES, createWallsForShape, calculateRoomArea } from '../dataModels/models';
import { ROOM_SHAPES, Room, WALL_RENDER_PROPERTIES, calculateRoomArea, createWallsForShape } from '../dataModels';
import { useApp } from '../stateMgmt/AppContext';
import { actions } from '../stateMgmt/actions'; 
export function RoomCreationDialog() {
  const [shape, setShape] = useState(ROOM_SHAPES.RECTANGLE);
  const [width, setWidth] = useState(3);
  const [height, setHeight] = useState(4);
  const { setPendingRoomPosition, pendingRoomPosition, setSelectedRoomId, currentFloorId, dispatch, ui: { gridSize } } = useApp();
  

  const handleClose = (roomId = undefined) => {
    setPendingRoomPosition(false);
    setSelectedRoomId(roomId);
  };
  // Generate preview walls based on current inputs
  const previewWalls = useMemo(() => {
    if (width <= 0 || height <= 0) return [];
    
    let walls = createWallsForShape(shape, { width, height });
    return walls;
  }, [shape, width, height]);

  const handleCreate = () => {
    if (!pendingRoomPosition) return;
  
    const snap = (v) => Math.round(v / gridSize) * gridSize;
  
    // Calculate the minimum X and Y of the preview walls
    const allX = previewWalls.flatMap(w => [w.startPoint.x, w.endPoint.x]);
    const allY = previewWalls.flatMap(w => [w.startPoint.y, w.endPoint.y]);
    const minX = Math.min(...allX);
    const minY = Math.min(...allY);
  
    // SNAP the intended placement position
    const snappedX = snap(pendingRoomPosition.x);
    const snappedY = snap(pendingRoomPosition.y);
  
    // Offset so room origin lands on snapped grid
    const offsetX = snappedX - minX;
    const offsetY = snappedY - minY;
  
    // Apply offset to all walls
    const positionedWalls = previewWalls.map(wall => ({
      ...wall,
      startPoint: {
        x: wall.startPoint.x + offsetX,
        y: wall.startPoint.y + offsetY
      },
      endPoint: {
        x: wall.endPoint.x + offsetX,
        y: wall.endPoint.y + offsetY
      }
    }));
  
    // Create the room with snapped geometry
    const newRoom = Room('', '', positionedWalls);
  
    dispatch({
      type: actions.ADD_ROOM,
      payload: {
        room: newRoom,
        floorId: currentFloorId
      }
    });
  
    handleClose(newRoom.id);
  };

  // Calculate preview dimensions
  const previewSize = 400;
  let scale = 1;
  let offsetX = 0;
  let offsetY = 0;
  let centerX = 0;
  let centerY = 0;
  let area = 0;

  if (previewWalls.length > 0) {
    const allX = previewWalls.flatMap(w => [w.startPoint.x, w.endPoint.x]);
    const allY = previewWalls.flatMap(w => [w.startPoint.y, w.endPoint.y]);
    const minX = Math.min(...allX);
    const maxX = Math.max(...allX);
    const minY = Math.min(...allY);
    const maxY = Math.max(...allY);
    const roomWidth = maxX - minX;
    const roomHeight = maxY - minY;
    centerX = (minX + maxX) / 2;
    centerY = (minY + maxY) / 2;
    
    // Calculate scale to fit room in preview
    const scaleX = (previewSize) / Math.max(roomWidth, 1);
    const scaleY = (previewSize) / Math.max(roomHeight, 1);
    scale = Math.min(scaleX, scaleY, 50);
    
    // Offset to center the room
    offsetX = previewSize / 2 - centerX * scale;
    offsetY = previewSize / 2 - centerY * scale;
    
    area = calculateRoomArea(previewWalls);
  }

  return (
    <Dialog open={true} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">צור חדר חדש</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 3, pt: 2 }}>
          {/* Left side - Preview */}
          <Box sx={{ width: previewSize, minHeight: previewSize, flexShrink: 0 }}>
            <Typography variant="subtitle2" gutterBottom>תצוגה מקדימה</Typography>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Stage width={previewSize} height={previewSize}>
                <Layer>
                  <Group x={offsetX} y={offsetY} scaleX={scale * 1.15} scaleY={scale * 1.1}>
                    {/* Render walls */}
                    {previewWalls.map((wall, index) => {
                      const { strokeColor, fillColor, strokePattern, thickness } = WALL_RENDER_PROPERTIES[wall.type];
                      const startX = wall.startPoint.x;
                      const startY = wall.startPoint.y;
                      const endX = wall.endPoint.x;
                      const endY = wall.endPoint.y;
                      const dx = endX - startX;
                      const dy = endY - startY;
                      const length = Math.sqrt(dx * dx + dy * dy) || 1;
                      const perpX = (-dy / length) * (thickness / 2);
                      const perpY = (dx / length) * (thickness / 2);
                      
                      const wallPoints = [
                        startX + perpX, startY + perpY,
                        endX + perpX, endY + perpY,
                        endX - perpX, endY - perpY,
                        startX - perpX, startY - perpY
                      ];
                      
                      return (
                        <Group key={index}>
                          {/* Wall fill */}
                          {fillColor && (
                            <Line
                              points={wallPoints}
                              closed
                              fill={fillColor}
                              stroke={strokeColor}
                              strokeWidth={1}
                              strokeScaleEnabled={false}
                              dash={strokePattern}
                              listening={false}
                            />
                          )}
                          
                          {/* Wall outline */}
                          <Line
                            points={[startX, startY, endX, endY]}
                            stroke={strokeColor}
                            strokeWidth={thickness}
                            strokeScaleEnabled={false}
                            dash={strokePattern}
                            listening={false}
                          />
                          
                          {/* Wall length label */}
                          {length > 0.5 && (
                            <Text
                              x={(startX + endX) / 2 - 0.20}
                              y={(startY + endY) / 2 - 0.10}
                              text={`${length.toFixed(2)}m`}
                              fontSize={0.10}
                              fill="#666"
                              listening={false}
                            />
                          )}
                        </Group>
                      );
                    })}
                    
                    {/* Room area label */}
                    {area > 0 && (
                      <Text
                        x={centerX - 0.30}
                        y={centerY + 0.20}
                        text={`${area.toFixed(2)} m²`}
                        fontSize={0.10}
                        fill="#1976D2"
                        listening={false}
                      />
                    )}
                  </Group>
                </Layer>
              </Stage>
            </Box>
            {area > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                שטח: {area.toFixed(2)} m²
              </Typography>
            )}
          </Box>

          {/* Right side - Form */}
          <Box sx={{ flex: 1 }}>
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>צורת החדר</InputLabel>
                <Select
                  value={shape}
                  onChange={(e) => setShape(e.target.value)}
                  label="צורת החדר"
                >
                  <MenuItem value={ROOM_SHAPES.RECTANGLE}>□</MenuItem>
                  <MenuItem value={ROOM_SHAPES.L_SHAPE}>L</MenuItem>
                  <MenuItem value={ROOM_SHAPES.U_SHAPE}>U</MenuItem>
                </Select>
              </FormControl>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="רוחב (מטרים)"
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0.5, step: 0.1 }}
                  fullWidth
                />
                <TextField
                  label="אורך (מטרים)"
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0.5, step: 0.1 }}
                  fullWidth
                />
              </Stack>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>ביטול</Button>
        <Button variant="contained" color="primary" onClick={handleCreate}>
          צור חדר
        </Button>
      </DialogActions>
    </Dialog>
  );
}

